import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Play, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminVoices() {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [audio, setAudio] = useState(null);
  const [names, setNames] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/voices");
      setVoices(data);
      setNames(Object.fromEntries(data.map((v) => [v.voice_key, v.custom_name || ""])));
    } catch {
      toast.error("Failed to load voices");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (voiceKey, payload, msg) => {
    try {
      await api.put(`/voices/${encodeURIComponent(voiceKey)}`, payload);
      toast.success(msg);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const stopAudio = () => {
    if (audio) { audio.pause(); audio.currentTime = 0; }
    setAudio(null);
    setPlaying(null);
  };

  const togglePlay = (v) => {
    if (playing === v.voice_key) { stopAudio(); return; }
    stopAudio();
    const token = localStorage.getItem("cs_admin_token") || localStorage.getItem("cs_token");
    const a = new Audio(v.preview_url);
    a.onended = () => { setPlaying(null); setAudio(null); };
    a.onerror = () => { toast.error("Preview unavailable"); setPlaying(null); setAudio(null); };
    a.play().catch(() => { toast.error("Preview unavailable"); setPlaying(null); setAudio(null); });
    setAudio(a);
    setPlaying(v.voice_key);
  };

  return (
    <div className="space-y-6" data-testid="admin-voices-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Admin</p>
        <h1 className="text-3xl font-bold tracking-tight mt-1">Voice Management</h1>
        <p className="text-sm text-muted-foreground mt-1">Rename voices for clients and control which appear in the Voice Gallery.</p>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading voices…
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-wider w-12"></TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Voice</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Client display name</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Style</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Visible to clients</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {voices.map((v) => (
                <TableRow key={v.voice_key} className="border-border hover:bg-muted/40" data-testid={`admin-voice-row-${v.voice_key}`}>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 p-0"
                      disabled={!v.has_preview}
                      title={v.has_preview ? "Play preview" : "Preview not yet published for this voice"}
                      onClick={() => togglePlay(v)}
                      data-testid={`admin-voice-play-${v.voice_key}`}
                    >
                      {playing === v.voice_key ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="font-semibold text-sm">{v.name}</div>
                    <div className="text-[10px] text-muted-foreground capitalize">{[v.gender, v.accent].filter(Boolean).join(" · ")}</div>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={names[v.voice_key] ?? ""}
                      placeholder={v.name}
                      className="h-8 max-w-[220px]"
                      data-testid={`admin-voice-name-${v.voice_key}`}
                      onChange={(e) => setNames({ ...names, [v.voice_key]: e.target.value })}
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== (v.custom_name || "")) {
                          save(v.voice_key, { custom_name: val || null }, "Display name saved");
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate">{v.description}</TableCell>
                  <TableCell className="text-right">
                    <Switch
                      checked={!v.hidden}
                      data-testid={`admin-voice-visible-${v.voice_key}`}
                      onCheckedChange={(checked) => save(v.voice_key, { hidden: !checked }, checked ? "Voice visible to clients" : "Voice hidden from clients")}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
